import { AccountingLangFactory } from "@bokio/lang";
import * as m from "@bokio/mobile-web-shared/core/model/model";

import type { CardRadioOption } from "@bokio/designsystem/components/CardRadioGroup/CardRadioGroup";

import ReceiptCategoryType = m.Core.Contracts.Todo.ReceiptCategoryType;

export interface CategoryExample {
	text: string;
	isNegative?: boolean;
}

export interface CategoryWithExamples extends CardRadioOption {
	examples?: CategoryExample[];
}

export interface BookkeepingCategoryOptions {
	standardEvents: CategoryWithExamples[];
	specialEvents: CategoryWithExamples[];
}

export const getReceiptCategoryOptions = (): BookkeepingCategoryOptions => {
	const accountingLang = AccountingLangFactory();

	const standardEvents: CategoryWithExamples[] = [
		{
			testId: "BookkeepingCategoryStep_SupplierInvoice_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_SupplierInvoice_Title,
					description: accountingLang.BookkeepCategory_SupplierInvoice_Desc,
				},
			],
			value: ReceiptCategoryType.supplier,
			examples: [
				{
					text: accountingLang.BookkeepCategory_SupplierInvoice_Example1,
				},
				{
					text: accountingLang.BookkeepCategory_SupplierInvoice_Example2,
					isNegative: true,
				},
			],
		},
		{
			testId: "BookkeepingCategoryStep_TransactionReceipt_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_TransactionReceipt_Title,
					description: accountingLang.BookkeepCategory_TransactionReceipt_Desc,
				},
			],
			value: ReceiptCategoryType.transactionReceipt,
			examples: [
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example1,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example2,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example3,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example4,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example5,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example6,
					isNegative: true,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example7,
					isNegative: true,
				},
				{
					text: accountingLang.BookkeepCategory_TransactionReceipt_Example8,
					isNegative: true,
				},
			],
		},
	];

	const specialEvents: CategoryWithExamples[] = [
		{
			testId: "BookkeepingCategoryStep_ReceiptOrExpense_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Title,
					description: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Desc,
				},
			],
			value: ReceiptCategoryType.expense,
			examples: [
				{
					text: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Example1,
				},
				{
					text: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Example2,
				},
				{
					text: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Example3,
				},
				{
					text: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Example4,
				},
			],
		},
		{
			testId: "BookkeepingCategoryStep_Income_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Title,
					description: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Desc,
				},
			],
			value: ReceiptCategoryType.income,
			examples: [
				{
					text: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Example1,
				},
				{
					text: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Example2,
				},
				{
					text: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Example3,
				},
				{
					text: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Example4,
				},
				{
					text: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Example5,
				},
			],
		},
		{
			testId: "BookkeepingCategoryStep_Other_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_OtherAdjustment_Title,
					description: accountingLang.BookkeepCategory_OtherAdjustment_Desc,
				},
			],
			value: ReceiptCategoryType.other,
		},
	];

	return { standardEvents, specialEvents };
};

export const getBookkeepingEventCategoryOptions = (): BookkeepingCategoryOptions => {
	const accountingLang = AccountingLangFactory();

	const standardEvents: CategoryWithExamples[] = [
		{
			testId: "BookkeepingCategoryStep_SupplierInvoice_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_SupplierInvoice_Title,
					description: accountingLang.BookkeepCategory_SupplierInvoice_Desc,
				},
			],
			value: ReceiptCategoryType.supplier,
		},
	];

	const specialEvents: CategoryWithExamples[] = [
		{
			testId: "BookkeepingCategoryStep_ReceiptOrExpense_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Title,
					description: accountingLang.BookkeepCategory_PurchaseFromOtherAccount_Desc,
				},
			],
			value: ReceiptCategoryType.expense,
		},
		{
			testId: "BookkeepingCategoryStep_Income_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Title,
					description: accountingLang.BookkeepCategory_IncomeFromOtherAccount_Desc,
				},
			],
			value: ReceiptCategoryType.income,
		},
		{
			testId: "BookkeepingCategoryStep_Other_RadioFieldLabel",
			subItems: [
				{
					key: "label",
					title: accountingLang.BookkeepCategory_OtherAdjustment_Title,
					description: accountingLang.BookkeepCategory_OtherAdjustment_Desc,
				},
			],
			value: ReceiptCategoryType.other,
		},
	];

	return { standardEvents, specialEvents };
};
